import React, { useContext as useContextCore } from 'react'
import type {
  DirectionEnum,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMemberSort,
  PivotLayoutValueColumn,
  PivotLayoutValueSort,
} from '@ztechapps/common.contract'
import type { PivotCellRenderer } from '../constants/types'
import type { PivotData, PivotFilterSetting, PivotHeaderCell, PivotLeafColumn, PivotResult } from '../pivot/types'

/** Pivot grid data shared with header and row cells. */
export type PivotGridContextType = {
  result: PivotResult
  headerRows: PivotHeaderCell[][]
  leafColumns: PivotLeafColumn[]
  isExpanded: (key: string) => boolean
  onToggle: (key: string) => void
  renderCell?: PivotCellRenderer
  data: PivotData
  filters: PivotLayoutFilter[]
  timeZone: string
  onFilter: (field: PivotLayoutField, setting: PivotFilterSetting | null) => void
  measuredWidths: number[]
  fixedWidths: (number | undefined)[]
  onColumnDrag: (index: number, width: number) => void
  onColumnResize: (index: number, width: number) => void
  onColumnReset: (index: number) => void
  memberSorts: PivotLayoutMemberSort[]
  onMemberSort: (field: PivotLayoutField, direction?: DirectionEnum) => void
  valueSort?: PivotLayoutValueSort
  onValueSort: (valueColumn: PivotLayoutValueColumn) => void
  isCellSelected: (rowIndex: number, column: number) => boolean
}
const context = React.createContext<PivotGridContextType>({} as PivotGridContextType)

/** Props for the pivot grid context provider. */
export type PivotGridProviderProps = { children: React.ReactNode } & PivotGridContextType

/** Provides pivot grid data to header and row cells. */
export const PivotGridProvider = ({ children, ...rest }: PivotGridProviderProps) => {
  return <context.Provider value={{ ...rest }}>{children}</context.Provider>
}

/** Reads the pivot grid context. */
export const usePivotGridContext = () => useContextCore(context)
